/**
 * LooseRelationshipFilterPane — collapsible wrapper around FilterWizard for one
 * side of a loose foreign key (source or target). Owns no SQL; it just edits a
 * FilterGroup[] in place via the parent's onFiltersChange.
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown, Info } from 'lucide-react';
import { FilterWizard } from './FilterWizard';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FilterGroup, TableExplorerColumn } from '../../../core/types/workspaceTypes';

export type LooseRelationshipFilterPaneProps = {
  side: 'source' | 'target';
  filters: FilterGroup[];
  onFiltersChange: (next: FilterGroup[]) => void;
  columns: TableExplorerColumn[];
  /** Required for FilterWizard's FK lookup operator. */
  connectionId: string;
  database: string;
  tableSchema: string;
  tableName: string;
};

const LABEL = { source: 'Source filter', target: 'Target filter' } as const;
const TOOLTIP = {
  source: 'This FK only applies to source rows matching this filter.',
  target: 'Only target rows matching this filter are considered linked.',
} as const;

export function LooseRelationshipFilterPane({
  side,
  filters,
  onFiltersChange,
  columns,
  connectionId,
  database,
  tableSchema,
  tableName,
}: LooseRelationshipFilterPaneProps) {
  const conditionCount = filters.reduce((acc, g) => acc + g.conditions.length, 0);
  const [expanded, setExpanded] = useState(conditionCount > 0);

  return (
    <div
      className="flex flex-col gap-1 border rounded p-2"
      data-testid={`loose-fk-${side}-filter-pane`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium text-left"
        aria-expanded={expanded}
        data-testid={`loose-fk-${side}-filter-toggle`}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>{LABEL[side]}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Info className="h-3 w-3 text-muted-foreground" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{TOOLTIP[side]}</TooltipContent>
        </Tooltip>
        <span className="text-muted-foreground ml-1">
          {conditionCount === 0
            ? '(no filter)'
            : `(${conditionCount} condition${conditionCount === 1 ? '' : 's'})`}
        </span>
      </button>

      {expanded && (
        <>
          <div className="mt-1">
            <FilterWizard
              filters={filters}
              columns={columns}
              onFiltersChange={onFiltersChange}
              connectionId={connectionId}
              database={database}
              tableSchema={tableSchema}
              tableName={tableName}
            />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">{TOOLTIP[side]}</div>
        </>
      )}
    </div>
  );
}
